import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, ArrowLeft, ArrowRight, Loader2, Crown, Infinity, Sparkles } from 'lucide-react';
import { Logo } from '@/components/Logo';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth.jsx';

export default function PricingPage() {
    const [plans, setPlans] = useState({});
    const [annual, setAnnual] = useState(true); // default to annual (better story for both buyer and acquirer)
    const [loadingPlan, setLoadingPlan] = useState(null);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        api.get('/billing/plans').then((r) => setPlans(r.data.plans || {})).catch(() => {});
    }, []);

    const tiers = [
        {
            key: 'solo',
            name: 'Solo',
            desc: 'For owner-operators and 1-truck shops',
            tagline: 'Get on the ladder.',
            color: 'border-slate-200',
        },
        {
            key: 'crew',
            name: 'Crew',
            desc: 'For small crews booking real volume',
            tagline: 'Unlimited everything. Most popular.',
            highlight: true,
            color: 'border-amber-500 shadow-xl',
        },
        {
            key: 'business',
            name: 'Business',
            desc: 'For multi-crew operations and larger shops',
            tagline: 'Unlimited seats. Scale without friction.',
            color: 'border-slate-200',
        },
    ];

    const features = {
        // These mirror the landing page (/) bullets exactly — landing is the
        // source of truth so a prospect never sees a promise on /pricing that
        // they don't also see on /.
        solo: [
            { icon: 'check', text: '100 lead lookups / month (resets monthly · top-ups never expire)' },
            { icon: 'check', text: '1 seat included' },
            { icon: 'check', text: 'Unlimited quotes, PDF + e-sign' },
            { icon: 'check', text: '24-month storm history per property' },
            { icon: 'check', text: 'Live PDF preview & auto-filled costs' },
            { icon: 'check', text: 'Cancel anytime' },
        ],
        crew: [
            { icon: 'infinity', text: 'Unlimited lead lookups & AI estimates' },
            { icon: 'check', text: '3 seats included' },
            { icon: 'check', text: 'Add up to 3 extra seats at $29/mo each' },
            { icon: 'check', text: 'Unlimited quotes, PDF + e-sign' },
            { icon: 'check', text: '24-month storm history per property' },
            { icon: 'check', text: 'Live PDF preview & auto-filled costs' },
            { icon: 'check', text: 'Cancel anytime' },
        ],
        business: [
            { icon: 'infinity', text: 'Unlimited lead lookups & AI estimates' },
            { icon: 'check', text: '6 seats included' },
            { icon: 'crown', text: 'Unlimited extra seats at $29/mo each' },
            { icon: 'check', text: 'Unlimited quotes, PDF + e-sign' },
            { icon: 'check', text: '24-month storm history per property' },
            { icon: 'check', text: 'Live PDF preview & auto-filled costs' },
            { icon: 'check', text: 'Cancel anytime' },
        ],
    };

    const startCheckout = async (planKey) => {
        if (!user) {
            navigate('/signup');
            return;
        }
        setLoadingPlan(planKey);
        try {
            const r = await api.post('/billing/checkout/subscription', { plan_key: planKey, origin_url: window.location.origin });
            // Backend flags trial_included=false when the user has already
            // consumed a trial on a prior org / same email / same-family
            // domain. Nudge the user before Stripe charges them so it
            // doesn't feel like a bait-and-switch.
            if (r.data && r.data.trial_included === false) {
                const reason = r.data.trial_ineligible_reason;
                const msg =
                    reason === 'email_already_used_trial' || reason === 'org_already_used_trial'
                        ? "Heads up — you've already used your free trial. This checkout will charge your card immediately."
                        : reason === 'email_domain_not_trial_eligible'
                            ? 'Your email provider is not eligible for the free trial — your card will be charged immediately at checkout.'
                            : 'Free trial not available on this checkout — your card will be charged immediately.';
                toast.info(msg, { duration: 6000 });
            }
            window.location.href = r.data.url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to start checkout');
            setLoadingPlan(null);
        }
    };

    const renderFeatureIcon = (kind) => {
        if (kind === 'infinity') return <Infinity className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" strokeWidth={2.5} />;
        if (kind === 'crown') return <Crown className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />;
        return <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />;
    };

    return (
        <div className="min-h-screen bg-background">
            <header className="bg-white border-b border-slate-200">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                    <Link to="/"><Logo /></Link>
                    {user ? (
                        <Button asChild variant="ghost" data-testid="pricing-back-to-app"><Link to="/app/dashboard"><ArrowLeft className="h-4 w-4 mr-1" /> Back to app</Link></Button>
                    ) : (
                        <Button asChild variant="ghost"><Link to="/login">Sign in</Link></Button>
                    )}
                </div>
            </header>

            <section className="py-12 sm:py-16">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto">
                        <h1 className="text-3xl sm:text-4xl font-display tracking-tight text-slate-900">Quote roofs in seconds, not hours.</h1>
                        <p className="mt-3 text-slate-600">Type an address. We pull the satellite imagery, measure the roof with AI, and have a quote ready to send before your competitor calls back.</p>
                    </div>

                    {/* 7-day free trial notice — every plan starts with a
                        7-day CC-upfront free trial. Message stays visible
                        regardless of auth state so the offer is obvious. */}
                    <div className="mt-8 max-w-2xl mx-auto" data-testid="pricing-trial-cta">
                        <div className="rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white p-5 sm:p-6 shadow-sm">
                            <div className="flex items-start gap-3">
                                <div className="h-10 w-10 rounded-lg bg-amber-500 text-slate-950 flex items-center justify-center shrink-0">
                                    <Sparkles className="h-5 w-5" />
                                </div>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">Every plan · 7-day free trial</span>
                                    </div>
                                    <h2 className="text-xl sm:text-2xl font-display font-semibold text-slate-900">
                                        7 days free. Cancel any time before day 8 &mdash; no charge.
                                    </h2>
                                    <p className="mt-1 text-sm text-slate-600">
                                        Card required at checkout to prevent abuse. Stripe will not charge you for 7 full days. After the trial converts you keep every feature and every credit already granted.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 flex items-center justify-center gap-3" data-testid="pricing-billing-toggle">
                        <button
                            type="button"
                            onClick={() => setAnnual(false)}
                            className={`px-3 py-1.5 text-sm rounded-md ${!annual ? 'bg-slate-900 text-white font-semibold' : 'text-slate-500 hover:text-slate-900'}`}
                            data-testid="pricing-toggle-monthly"
                        >
                            Monthly
                        </button>
                        <button
                            type="button"
                            onClick={() => setAnnual(true)}
                            className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1.5 ${annual ? 'bg-slate-900 text-white font-semibold' : 'text-slate-500 hover:text-slate-900'}`}
                            data-testid="pricing-toggle-annual"
                        >
                            Annual <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${annual ? 'bg-amber-500 text-slate-950' : 'bg-emerald-50 text-emerald-700'} font-semibold`}>SAVE 20%</span>
                        </button>
                    </div>

                    <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
                        {tiers.map((tier) => {
                            const planKey = `${tier.key}_${annual ? 'annual' : 'monthly'}`;
                            const plan = plans[planKey];
                            if (!plan) return null;
                            const monthlyEquiv = annual ? (plan.amount / 100 / 12) : (plan.amount / 100);
                            const annualTotal = plan.amount / 100;
                            const annualSavings = annual ? (() => {
                                const m = plans[`${tier.key}_monthly`];
                                if (!m) return null;
                                return (m.amount * 12 - plan.amount) / 100;
                            })() : null;
                            const tierFeatures = features[tier.key] || [];
                            return (
                                <Card key={tier.key} className={`relative flex flex-col ${tier.color}`} data-testid={`pricing-card-${tier.key}`}>
                                    {tier.highlight && (
                                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                            <Badge className="bg-amber-500 text-slate-950 font-semibold border-0">⭐ Most popular</Badge>
                                        </div>
                                    )}
                                    <CardHeader className="pb-2">
                                        <CardTitle className="font-display text-2xl text-slate-900 flex items-center gap-2">
                                            {tier.name}
                                            {tier.key === 'business' && <Crown className="h-5 w-5 text-amber-600" />}
                                        </CardTitle>
                                        <div className="text-sm text-slate-500">{tier.desc}</div>
                                        <div className="text-xs text-amber-700 font-medium mt-0.5">{tier.tagline}</div>
                                    </CardHeader>
                                    <CardContent className="flex-1 flex flex-col">
                                        <div className="mt-2 flex items-baseline gap-1">
                                            <span className="text-4xl font-display font-semibold text-slate-900 tabular-nums">${monthlyEquiv.toFixed(0)}</span>
                                            <span className="text-slate-500">/mo</span>
                                        </div>
                                        <div className="text-xs text-slate-500 mt-1">
                                            {annual ? <>Billed <span className="font-medium">${annualTotal.toLocaleString()}</span> once a year</> : 'Billed monthly'}
                                            {annualSavings && annualSavings > 0 && (
                                                <span className="ml-2 text-emerald-700 font-medium">Save ${annualSavings.toLocaleString()}/yr</span>
                                            )}
                                        </div>
                                        <ul className="mt-5 space-y-2 text-sm text-slate-700 flex-1">
                                            {tierFeatures.map((f, i) => (
                                                <li key={i} className="flex items-start gap-2">
                                                    {renderFeatureIcon(f.icon)}
                                                    <span>{f.text}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <Button
                                            onClick={() => startCheckout(planKey)}
                                            disabled={loadingPlan === planKey}
                                            className={`w-full mt-6 ${tier.highlight ? 'bg-amber-500 hover:bg-amber-400 text-slate-950' : 'bg-slate-900 hover:bg-slate-800 text-white'} font-semibold`}
                                            data-testid={`pricing-checkout-${tier.key}-${annual ? 'annual' : 'monthly'}`}
                                        >
                                            {loadingPlan === planKey ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Redirecting…</>) : `Start ${tier.name} — 7-day free trial`}
                                        </Button>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>

                    <div className="mt-10 max-w-3xl mx-auto text-center space-y-2">
                        <p className="text-xs text-slate-500">Secure checkout by Stripe. Every plan starts with a 7-day free trial — card required, no charge for 7 days, cancel anytime from the billing portal. Prices in USD.</p>
                        <p className="text-xs text-slate-500">
                            Crew &amp; Business plans have <b>unlimited</b> AI roof estimates and lead lookups. We absorb the API costs so you can quote as many homes as you want.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
}
